import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import './User.css';

function UserIndex() {
  const [data, setData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const { isAuthenticated, companyId, logout, token, permissions } = useAuth();
  const hasCreateAdministrationPermission = permissions.includes('Create Administration');
  const hasEditAdministrationPermission = permissions.includes('Edit Administration');
  const hasDeleteAdministrationPermission = permissions.includes('Delete Administration');

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    fetchtableData();
  }, [isAuthenticated, companyId, token]);

  const fetchtableData = () => {
    fetchData('UserApi', `/${companyId}`, token)
      .then((response) => {
        if (response.responseCode === '1111') setData(response.response_data);
      })
      .catch((error) => console.error('Error fetching User Details:', error));
  };

  const filteredData = data.filter((item) =>
    Object.values(item).some((v) => String(v).toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleDelete = () => {
    deleteData('UserApi', `${deleteItemId}`, token)
      .then(() => { fetchtableData(); setDeleteItemId(null); setConfirmDialogOpen(false); })
      .catch((error) => console.error('Error deleting record:', error));
  };

  const handleActivate = (itemId) => {
    activateData('UserApi', `${itemId}`, token)
      .then(() => fetchtableData())
      .catch(() => {});
  };

  return (
    <div className="user-container">
      <div className="user-header-card">
        <div className="user-header-content">
          <div className="user-title-section">
            <span className="user-title-icon"><PeopleIcon fontSize="small" /></span>
            <h1 className="user-title">Users</h1>
          </div>
          <div className="user-header-actions">
            <div className={'user-search-wrapper' + (searchOpen ? ' open' : '')}>
              <span className="user-search-icon" onClick={() => setSearchOpen((o) => !o)}>
                <SearchIcon fontSize="small" />
              </span>
              {searchOpen && (
                <input
                  autoFocus
                  className="user-search-input"
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onBlur={() => { if (!searchQuery) setSearchOpen(false); }}
                />
              )}
            </div>
            {hasCreateAdministrationPermission && (
              <Link to="/userCreate" className="usr-btn usr-btn-create">
                <AddIcon style={{ fontSize: 14 }} />&nbsp;New User
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="user-table-wrapper">
        <TableContainer component={Paper} className="user-table-container" elevation={0}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell className="user-th">Sl.No</TableCell>
                <TableCell className="user-th">First Name</TableCell>
                <TableCell className="user-th">Last Name</TableCell>
                <TableCell className="user-th">User Name</TableCell>
                <TableCell className="user-th">Contact Number</TableCell>
                <TableCell className="user-th">Email</TableCell>
                <TableCell className="user-th" align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((item, index) => (
                <TableRow key={item.Id} className={'user-row' + (!item.IsActive ? ' user-row-inactive' : '')}>
                  <TableCell className="user-td">{index + 1}</TableCell>
                  <TableCell className="user-td">{item.FirstName}</TableCell>
                  <TableCell className="user-td">{item.LastName}</TableCell>
                  <TableCell className="user-td">{item.UserName}</TableCell>
                  <TableCell className="user-td">{item.ContactNumber}</TableCell>
                  <TableCell className="user-td">{item.Email}</TableCell>
                  <TableCell className="user-td" align="center">
                    <div className="user-action-btns">
                      <Link to={`/userDetail/${item.Id}`}>
                        <IconButton className="user-icon-btn user-icon-detail" size="small" title="Detail">
                          <ArrowForwardIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Link>
                      {hasEditAdministrationPermission && (
                        <Link to={`/userEdit/${item.Id}`}>
                          <IconButton className="user-icon-btn user-icon-edit" size="small" title="Edit">
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Link>
                      )}
                      {hasDeleteAdministrationPermission && (
                        <IconButton
                          className={'user-icon-btn ' + (item.IsActive ? 'user-icon-delete' : 'user-icon-activate')}
                          size="small"
                          title={item.IsActive ? 'Delete' : 'Activate'}
                          onClick={() => item.IsActive ? (setDeleteItemId(item.Id), setConfirmDialogOpen(true)) : handleActivate(item.Id)}
                        >
                          {item.IsActive ? <DeleteIcon sx={{ fontSize: 16 }} /> : <ActivateIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} PaperProps={{ style: { borderRadius: '10px', padding: '4px' } }}>
        <DialogTitle style={{ fontSize: '16px', fontWeight: 600 }}>Delete Record?</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ fontSize: '13px' }}>Are you sure you want to delete this record?</DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: '12px 20px', gap: '8px' }}>
          <button className="usr-btn usr-btn-cancel" onClick={() => setConfirmDialogOpen(false)}>Cancel</button>
          <button className="usr-btn usr-btn-delete" onClick={handleDelete}>Delete</button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default UserIndex;
